import { get, isObject } from 'lodash';
import { ALL_TRAFFIC } from '@utils/constants';
import { compareSchemaOptions, compareSchemaChartSeriesOptions } from '@utils/compareSchema';
import featureNames from '@utils/featureToggle/constants';
import { generateColor } from '@utils/getColor';
import { jsonTransform, getColor, getFilterFromRules, isGrandfatherFilter } from '@utils';
import { INVERTED_METRIC_SWITCH_MAP, getMetricUnit } from '@utils/metrics';
import tooltipPosition from './tooltipPosition';

/**
 * @param {number} len     Length of array list
 */
const makeEmptyArrayList = (len = 0) => {
  const newArr = [];
  for (let i = 0; i < len; i += 1) {
    newArr.push([]);
  }
  return newArr;
};

/**
 * @param {array} filterBars     Array list of filter bars
 * @param {array} args           Total list of filter objects
 */
const parseCompareData = (config, args = [], features = {}, productMetrics) => {
  const filterBars = [...config.filterBars, ...config.savedFilters];

  const LENGTH = args.length;
  const coreMetrics = Object.keys(get(args[0], '[0].totals') || {});

  const metrics: any[] = [];
  config.metricList.forEach((metric) => {
    if (coreMetrics.includes(metric)) {
      metrics.push(metric);
      if (metric in INVERTED_METRIC_SWITCH_MAP) {
        metrics.push(INVERTED_METRIC_SWITCH_MAP[metric]);
      }
    }
  });

  const compareObj = metrics.reduce((obj, metric) => {
    const arr = makeEmptyArrayList(LENGTH);
    return { ...obj, [metric]: arr };
  }, {});

  const totalObj = metrics.reduce((obj, metric) => ({ ...obj, [metric]: [] }), {});

  args.forEach((arg, index) => {
    const dataArr = get(arg, '[0].data') || [];
    const totals = get(arg, '[0].totals') || {};
    dataArr.forEach((item) => {
      metrics.forEach((metric) => {
        compareObj[metric][index].push([item.timestamp, item.results[metric]]);
      });
    });

    metrics.forEach((metric) => {
      totalObj[metric].push(totals[metric]);
    });
  });

  const filterMetaList = [];
  const genColor = generateColor();
  const colors = filterBars.map((bar) => {
    if (Object.values(bar.filters).length) {
      let flexFilter = false;
      Object.values(bar.filters).forEach((rule) => {
        if (Object.keys(rule).includes('_order')) {
          flexFilter = true;
        }
      });
      const filter = getFilterFromRules(bar.filters, flexFilter);
      const name = bar.name || filter.name;
      const oldPulseFilter = isGrandfatherFilter(filter, features[featureNames.CUSTOM_TAGS]);
      filterMetaList.push({ name, oldPulseFilter });
      return genColor.next(name).value;
    }

    filterMetaList.push({ name: ALL_TRAFFIC });
    return genColor.next(ALL_TRAFFIC).value;
  });

  if (!filterBars.length) {
    filterMetaList.push({ name: ALL_TRAFFIC });
    colors.push(genColor.next(ALL_TRAFFIC).value);
  }

  metrics.forEach((metric, index) => {
    const targetSchema = compareSchemaOptions({
      [metric]: productMetrics[metric],
      id: index,
    });

    const base = jsonTransform({}, targetSchema, {
      'totals.stdDeviation': [0],
      getColor,
    });

    const data = compareObj[metric];
    if (!Array.isArray(data)) {
      return;
    }

    base.series = data.map((item, i) => {
      const targetSchemaSeries = compareSchemaChartSeriesOptions({
        data: item,
      });
      return jsonTransform(item, targetSchemaSeries, {
        filterColor: [colors[i]],
        'totals.stdDeviation': [0],
      });
    });

    base.tooltip.position = tooltipPosition;
    const metricUnit = getMetricUnit({ metricKey: metric });
    if (metricUnit === '%') {
      if (isObject(base.yAxis)) {
        base.yAxis.splitNumber = 5;
      }
    }

    compareObj[metric] = base;
  });

  return {
    dataObj: compareObj,
    colors,
    filterMetaList,
    totalObj,
    metrics,
  };
};

export default parseCompareData;
